import { MapComponent } from "@/services/map-management/map-helper";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

export interface ElementDimensions {
    width: number;
    height: number;
}

export interface ElementPosition {
    top: number;
    left: number;
    bottom: number;
    right: number;
}

export interface UseElementDimensionsOptions {
    /**
     * Optional: External ref to measure (if you already have a ref).
     * If not provided, the hook creates and returns a new ref.
     */
    ref?: RefObject<HTMLElement | null>;
    /**
     * Whether measurement should be active (default: true).
     * When false, dimensions/position reset to zero/null.
     */
    enabled?: boolean;
    /**
     * Whether to listen to Leaflet map movement (default: false).
     * Enable this for elements positioned relative to the map.
     */
    respondToMapMovement?: boolean;
}

export interface UseElementDimensionsReturn {
    dimensions: ElementDimensions;
    position: ElementPosition | null;
    ref?: RefObject<HTMLElement | null>;
}
/**
 * Hook for measuring element dimensions and position with responsive support.
 *
 * Automatically tracks:
 * - Element resize (via ResizeObserver)
 * - Window resize
 * - Map movement (optional, for overlays on Leaflet maps)
 *
 * Supports external refs as well as internal ref creation.
 * @param options - Configuration for measurement behavior
 * @returns Dimensions, position, and ref to attach to element
 */
export const useElementDimensions = (options: UseElementDimensionsOptions = {}): UseElementDimensionsReturn => {
    const { ref: externalRef, enabled = true, respondToMapMovement = false } = options;

    const [dimensions, setDimensions] = useState<ElementDimensions>({ width: 0, height: 0 });
    const [position, setPosition] = useState<ElementPosition | null>(null);

    const rafRef = useRef<number | null>(null);
    const internalRef = useRef<HTMLElement | null>(null);
    const activeRef = externalRef ?? internalRef;

    const updateMeasurements = useCallback(() => {
        const element = activeRef.current;
        if (!element?.isConnected) return;

        const rect = element.getBoundingClientRect();

        // Update dimensions if changed
        setDimensions((prev) =>
            prev.width !== rect.width || prev.height !== rect.height
                ? { width: rect.width, height: rect.height }
                : prev,
        );

        // Update position if changed
        setPosition((prev) => {
            if (
                !prev ||
                prev.top !== rect.top ||
                prev.left !== rect.left ||
                prev.bottom !== rect.bottom ||
                prev.right !== rect.right
            ) {
                return { top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right };
            }
            return prev;
        });
    }, [activeRef]);

    useEffect(() => {
        const element = activeRef.current;

        // Reset measurements when disabled or no element
        if (!enabled || !element) {
            setDimensions({ width: 0, height: 0 });
            setPosition(null);
            return undefined;
        }

        // Initial measurement
        updateMeasurements();

        // ResizeObserver for element size changes
        const resizeObserver = new ResizeObserver(updateMeasurements);
        resizeObserver.observe(element);

        // Window resize with RAF throttling for performance
        const handleWindowResize = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(updateMeasurements);
        };
        window.addEventListener("resize", handleWindowResize, { passive: true });

        // Optional: Track map movement for map-based overlays
        if (respondToMapMovement) {
            MapComponent?.on("moveend", updateMeasurements);
        }

        // Cleanup
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", handleWindowResize);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (respondToMapMovement) MapComponent?.off("moveend", updateMeasurements);
        };
    }, [enabled, respondToMapMovement, activeRef, updateMeasurements]);

    return { dimensions, position, ref: activeRef };
};
